import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { DebugDrawerSection, useNativeMessagingContext } from "@WahooFitness/wahoo-offline-mfe";
import useUserAccountSettings from "@/hooks/useUserAccountSettings";
import useNativeHealth from "./useNativeHealth";
import useRecoveryHealthSources from "./useRecoveryHealthSources";
import { Row } from "./DebugRows";
import { statusColor } from "./debugStatusColor";
import { useLastRecoverySourceUpdateError } from "./recoverySourceDebugStore";

// Not localized on purpose: developer-only content behind DEBUG_DRAWER_FLAG.
// `enabled` is DEBUG_DRAWER_FLAG: the device list is only asked for while the drawer can show it.
export const useThirdPartySharingDebug = (enabled: boolean): DebugDrawerSection[] => {
  const { userAccountSettingsData, userAccountSettingsIsLoading, userAccountSettingsError } =
    useUserAccountSettings();
  const { nativeShareServiceStatus } = useNativeMessagingContext();
  const { nativeHealthFitnessAppId } = useNativeHealth();
  const lastUpdateError = useLastRecoverySourceUpdateError();
  const recoveryHealthSources = useRecoveryHealthSources(enabled);

  const cloudRecoveryFitnessAppId = userAccountSettingsData?.recovery_fitness_app_id;

  return useMemo<DebugDrawerSection[]>(
    () => [
      {
        key: "recoverySource",
        title: "Recovery source",
        data: {
          cloudRecoveryFitnessAppId,
          appleHealthFitnessAppId: nativeHealthFitnessAppId,
          lastUpdateError,
        },
        content: (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Row
              label="cloud recovery_fitness_app_id"
              value={
                userAccountSettingsIsLoading
                  ? "loading…"
                  : userAccountSettingsError
                    ? "error loading user_account_settings"
                    : String(cloudRecoveryFitnessAppId ?? "null")
              }
            />
            <Row
              label="Apple Health fitness_app_id (this environment)"
              value={nativeHealthFitnessAppId ?? "n/a on this platform"}
              color={
                nativeHealthFitnessAppId !== undefined &&
                cloudRecoveryFitnessAppId === nativeHealthFitnessAppId
                  ? "success.main"
                  : undefined
              }
            />
            {lastUpdateError ? (
              <>
                <Typography variant="prose-sm-bold" color="error.main" mt={1}>
                  Last update failed
                </Typography>
                <Row label="at" value={lastUpdateError.at} />
                <Row
                  label="requested recovery_fitness_app_id"
                  value={String(lastUpdateError.requestedFitnessAppId)}
                />
                <Row
                  label="HTTP status"
                  value={lastUpdateError.status ?? "n/a"}
                  color="error.main"
                />
                <Row label="message" value={lastUpdateError.message ?? "n/a"} />
                <Typography
                  variant="prose-sm"
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    m: 0,
                  }}
                >
                  {JSON.stringify(lastUpdateError.body ?? null, null, 2)}
                </Typography>
              </>
            ) : (
              <Typography variant="prose-sm" color="text.secondary" mt={1}>
                No failed update this session.
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: "nativeShareServiceStatus",
        title: "Native share service status",
        data: nativeShareServiceStatus,
        content: nativeShareServiceStatus ? (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Row label="type" value={nativeShareServiceStatus.type} />
            <Row
              label="status"
              value={nativeShareServiceStatus.status}
              color={statusColor(nativeShareServiceStatus.status)}
            />
            <Row
              label="capabilities"
              value={
                nativeShareServiceStatus.capabilities?.length
                  ? nativeShareServiceStatus.capabilities.join(", ")
                  : "[]"
              }
            />
          </Box>
        ) : (
          <Typography variant="prose-sm" color="text.secondary">
            No shareServiceStatus received from native.
          </Typography>
        ),
      },
      {
        key: "appleHealthDevices",
        title: "Apple Health devices",
        data: recoveryHealthSources,
        content: recoveryHealthSources ? (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {recoveryHealthSources.sources.map(({ id, name }) => (
              <Row key={id} label={name} value={id} />
            ))}
            {recoveryHealthSources.sources.length === 0 && (
              <Typography variant="prose-sm" color="text.secondary">
                No device with recovery data in the backfill window.
              </Typography>
            )}
            {recoveryHealthSources.incomplete && (
              <Typography variant="prose-sm" color="warning.main">
                Incomplete: a source query failed or timed out.
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="prose-sm" color="text.secondary">
            No device list received from native.
          </Typography>
        ),
      },
    ],
    [
      cloudRecoveryFitnessAppId,
      lastUpdateError,
      nativeHealthFitnessAppId,
      nativeShareServiceStatus,
      recoveryHealthSources,
      userAccountSettingsError,
      userAccountSettingsIsLoading,
    ]
  );
};
